import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { toQueryString } from "../../components/shared/table-filters/filterQueryUtils";
import type { GetUserBetsParams, UserBetsResponse } from "../../types/bets";

type StoredToken = { AccessToken?: string };

const DEFAULT_PAGINATION: UserBetsResponse = {
  items: [],
  pagination: {
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  },
};

const fetchUserBets = async (
  params: GetUserBetsParams,
): Promise<UserBetsResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const queryParams: Record<string, unknown> = {
    type: "sportsbook",
    page: params.page,
    start_date: params.start_date,
    end_date: params.end_date,
  };

  const betId = params.bet_id?.trim();

  if (params.page_size !== undefined && params.page_size !== null) {
    queryParams.page_size = params.page_size;
  }

  // Exact-ID mode is a backend override (PAMENG-1867): the response is always
  // page 1 with a null cursor and totals computed from the matched bet, so
  // cached totals / cursors must not be echoed back.
  if (!betId) {
    if (params.totals) {
      queryParams.totals = params.totals;
    }

    if (
      params.last_evaluated_key !== undefined &&
      params.last_evaluated_key !== null
    ) {
      queryParams.last_evaluated_key = params.last_evaluated_key;
    }
  }

  if (Array.isArray(params.status)) {
    if (params.status.length > 0) {
      queryParams.status = params.status.join(",");
    }
  } else if (params.status) {
    queryParams.status = params.status;
  }
  if (params.money_type) {
    queryParams.money_type = params.money_type;
  }
  if (params.bet_types && params.bet_types.length > 0) {
    queryParams.bet_types = params.bet_types.join(",");
  }
  if (betId) {
    queryParams.bet_id = betId;
  }

  const response = await instance.get(
    API_ROUTES.GET_USER_BETS.replace("{user_id}", params.user_id) +
      toQueryString(queryParams, { includeEmpty: true }),
  );

  const body = response?.data?.data;
  if (!body || typeof body !== "object") {
    return DEFAULT_PAGINATION;
  }

  return body as UserBetsResponse;
};

const serializeLastEvaluatedKey = (
  key: GetUserBetsParams["last_evaluated_key"],
): string => {
  if (key === undefined || key === null) return "";
  if (typeof key === "string") return key;
  return JSON.stringify(key);
};

export const useGetUserBets = (
  params: GetUserBetsParams | null,
): UseQueryResult<UserBetsResponse, Error> => {
  return useQuery<UserBetsResponse, Error>({
    queryKey: [
      "GET_USER_BETS",
      params?.user_id ?? "",
      params?.page ?? 1,
      params?.page_size ?? "",
      params?.start_date ?? "",
      params?.end_date ?? "",
      serializeLastEvaluatedKey(params?.last_evaluated_key),
      params?.totals ?? "",
      Array.isArray(params?.status)
        ? params.status.join(",")
        : (params?.status ?? ""),
      params?.money_type ?? "",
      params?.bet_types?.join(",") ?? "",
      params?.bet_id?.trim() ?? "",
    ],
    queryFn: () => fetchUserBets(params as GetUserBetsParams),
    enabled: Boolean(params?.user_id),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
